import React from 'react'
import axios from "axios";
import { useState } from "react";
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet-async';
import { API_URL } from "../../config";

// Environment variables for security - move these to server-side in production
const AUTH_CONFIG = {
  // This should come from environment variables in production
  PASSWORD_HASH: '74673dc86d2c997ac57d517a6a9a4615af9648ecc777f0b5b756c61af0b2d44e', // SHA-256 hash of ...
  TOKEN_EXPIRY_HOURS: 8,
  MAX_ATTEMPTS: 3,
  LOCKOUT_TIME_MINUTES: 15
};

export default function Intern() {
  const [roosters, setRoosters] = useState([]);
  const [programmas, setProgrammas] = useState([]);
  const [handleidingen, setHandleidingen] = useState([]);
  const [notulen, setNotulen] = useState([]);
  const [boekjes, setBoekjes] = useState([]);

  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);

  React.useEffect(() => {
    // Check if user is locked out
    const lockoutEnd = localStorage.getItem('intern_lockout');
    if (lockoutEnd && new Date().getTime() < parseInt(lockoutEnd)) {
      setIsLocked(true);
      return;
    }

    // Check if the user is already authenticated using a secure token
    const authToken = Cookies.get('intern_auth');
    const authExpiry = Cookies.get('intern_exp');
    
    if (authToken && authExpiry) {
      const now = new Date().getTime();
      if (now < parseInt(authExpiry) && validateAuthToken(authToken)) {
        setAuthenticated(true);
        loadInternData();
      } else {
        // Token expired or invalid, clear cookies
        Cookies.remove('intern_auth');
        Cookies.remove('intern_exp');
      }
    }

    // Get failed attempts from localStorage
    const failedAttempts = localStorage.getItem('intern_attempts');
    if (failedAttempts) {
      setAttempts(parseInt(failedAttempts));
    }
  }, []);

  const loadInternData = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/intern-bestanden?populate=deep`
      );
      
      setRoosters(response.data.data.attributes.roosters);
      setProgrammas(response.data.data.attributes.programmas);
      setHandleidingen(response.data.data.attributes.handleidingen);
      setNotulen(response.data.data.attributes.notulen);
      setBoekjes(response.data.data.attributes.boekjes);
    } catch (error) {
      setError('Failed to load data. Please try again.');
    }
  };

  const hashPassword = async (password) => {
    const msgBuffer = new TextEncoder().encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const generateAuthToken = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const validateAuthToken = (token) => {
    // Basic token validation - in production, this should verify against server
    return token && token.length === 64 && /^[a-f0-9]+$/i.test(token);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  const handleAuthenticate = async () => {
    if (isLocked) {
      setError('Account temporarily locked. Please try again later.');
      return;
    }

    if (attempts >= AUTH_CONFIG.MAX_ATTEMPTS) {
      const lockoutEnd = new Date().getTime() + (AUTH_CONFIG.LOCKOUT_TIME_MINUTES * 60000);
      localStorage.setItem('intern_lockout', lockoutEnd.toString());
      setIsLocked(true);
      setError(`Too many failed attempts. Account locked for ${AUTH_CONFIG.LOCKOUT_TIME_MINUTES} minutes.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const hashedInput = await hashPassword(password);
      
      if (hashedInput === AUTH_CONFIG.PASSWORD_HASH) {
        // Authentication successful
        const authToken = generateAuthToken();
        const expiryTime = new Date().getTime() + (AUTH_CONFIG.TOKEN_EXPIRY_HOURS * 60 * 60 * 1000);
        
        // Set secure cookies
        Cookies.set('intern_auth', authToken, { 
          expires: AUTH_CONFIG.TOKEN_EXPIRY_HOURS / 24, 
          secure: window.location.protocol === 'https:',
          sameSite: 'strict'
        });
        Cookies.set('intern_exp', expiryTime.toString(), { 
          expires: AUTH_CONFIG.TOKEN_EXPIRY_HOURS / 24,
          secure: window.location.protocol === 'https:',
          sameSite: 'strict'
        });
        
        setAuthenticated(true);
        setPassword(''); // Clear password from memory
        localStorage.removeItem('intern_attempts');
        localStorage.removeItem('intern_lockout');
        setAttempts(0);
        
        await loadInternData();
      } else {
        // Authentication failed
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        localStorage.setItem('intern_attempts', newAttempts.toString());
        setError(`Incorrect password. ${AUTH_CONFIG.MAX_ATTEMPTS - newAttempts} attempts remaining.`);
      }
    } catch (error) {
      setError('Authentication error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear cookies and reset state
    Cookies.remove('intern_auth');
    Cookies.remove('intern_exp');
    setAuthenticated(false);
    setPassword('');
    setRoosters([]);
    setProgrammas([]);
    setHandleidingen([]);
    setNotulen([]);
    setBoekjes([]);
  };


  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && !isLocked && password.trim()) {
      e.preventDefault();
      handleAuthenticate();
    }
  };

  return (
    <div>
      <Helmet>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
        <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet" />
        <title>Intern - Kriterion</title>
      </Helmet>
      
      {authenticated ? (
        // Content to display when authenticated
        <div className='intern'>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
            <h1>Intern</h1>
            <button className="logout-button" onClick={handleLogout}>Uitloggen</button>
          </div>
          
          <iframe 
            src="https://calendar.google.com/calendar/embed?src=qhuo9j77i8jj16ka33slcehvbk%40group.calendar.google.com&ctz=Europe%2FAmsterdam&src=zaalhuur%40gmail.com&ctz=Europe%2FAmsterdam&src=ov76p845d9ambetil62cr6nq50%40group.calendar.google.com&ctz=Europe%2FAmsterdam" 
            className="calendar"  
            title="calendar"
            sandbox="allow-scripts allow-same-origin"
          />
          
          {error && <div style={{color: 'red', marginBottom: '20px'}}>{error}</div>}
          
          <section className='intern-files'>
            <section>
              <h1>Roosters</h1>
              {roosters && roosters.map((rooster) =>
                rooster.__component === 'bestand.link'
                  ? <a href={rooster.url} key={rooster.url} rel="noopener noreferrer" target="_blank">{rooster.label}</a>
                  : <a href={rooster.bestand.data.attributes.url} key={rooster.bestand.data.attributes.name} rel="noopener noreferrer" target="_blank">{rooster.bestand.data.attributes.name}</a>
              )}
            </section>
            <section>
              <h1>Programma per week</h1>
              {programmas && programmas.map((programma) =>
                programma.__component === 'bestand.link'
                  ? <a href={programma.url} key={programma.url} rel="noopener noreferrer" target="_blank">{programma.label}</a>
                  : <a href={programma.bestand.data.attributes.url} key={programma.bestand.data.attributes.name} rel="noopener noreferrer" target="_blank">{programma.bestand.data.attributes.name}</a>
              )}
            </section>
            <section>
              <h1>Handleidingen</h1>
              {handleidingen && handleidingen.map((handleiding) =>
                handleiding.__component === 'bestand.link'
                  ? <a href={handleiding.url} key={handleiding.url} rel="noopener noreferrer" target="_blank">{handleiding.label}</a>
                  : <a href={handleiding.bestand.data.attributes.url} key={handleiding.bestand.data.attributes.name} rel="noopener noreferrer" target="_blank">{handleiding.bestand.data.attributes.name}</a>
              )}
            </section>
            <section>
              <h1>Notulen</h1>
              {notulen && notulen.map((notule) =>
                notule.__component === 'bestand.link'
                  ? <a href={notule.url} key={notule.url} rel="noopener noreferrer" target="_blank">{notule.label}</a>
                  : <a href={notule.bestand.data.attributes.url} key={notule.bestand.data.attributes.name} rel="noopener noreferrer" target="_blank">{notule.bestand.data.attributes.name}</a>
              )}
            </section>
            <section>
              <h1>Boekjes</h1>
              {boekjes && boekjes.map((boekje) =>
                boekje.__component === 'bestand.link'
                  ? <a href={boekje.url} key={boekje.url} rel="noopener noreferrer" target="_blank">{boekje.label}</a>
                  : <a href={boekje.bestand.data.attributes.url} key={boekje.bestand.data.attributes.name} rel="noopener noreferrer" target="_blank">{boekje.bestand.data.attributes.name}</a>
              )}
            </section>
          </section>
        </div>
      ) : (
        // Password input form
        <div style={{maxWidth: '400px', margin: '50px auto', padding: '20px'}}>
          <h1>Wachtwoord vereist</h1>
          
          {error && (
            <div style={{
              color: 'red', 
              backgroundColor: '#ffe6e6', 
              padding: '10px', 
              borderRadius: '5px',
              marginBottom: '15px'
            }}>
              {error}
            </div>
          )}
          
          {isLocked ? (
            <div style={{
              color: '#666',
              backgroundColor: '#f5f5f5',
              padding: '15px',
              borderRadius: '5px',
              textAlign: 'center'
            }}>
              Account temporarily locked due to multiple failed attempts.<br/>
              Please try again in {AUTH_CONFIG.LOCKOUT_TIME_MINUTES} minutes.
            </div>
          ) : (
            <div>
              <input
                type="password"
                placeholder="Vul intern wachtwoord in"
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={handleKeyDown}
                className='password-prompt'
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginBottom: '15px',
                  borderRadius: '5px',
                  border: '1px solid #ccc'
                }}
              />
              <button 
                onClick={handleAuthenticate} 
                className='password-prompt'
                disabled={loading || !password.trim()}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: loading ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Authenticating...' : 'Naar intern'}
              </button>
              
              {attempts > 0 && attempts < AUTH_CONFIG.MAX_ATTEMPTS && (
                <p style={{color: '#666', fontSize: '14px', marginTop: '10px'}}>
                  Failed attempts: {attempts}/{AUTH_CONFIG.MAX_ATTEMPTS}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
